import { Markdown, MarkdownSize } from '@vtblife/react-markdown';
import { Button } from '@vtblife/uikit';
import { Form, Formik } from '@vtblife/uikit-formik';
import React, { useEffect, useRef, useState } from 'react';
import { createDependencyValidator } from '@vtblife/form-renderer-kit/dist/components/form-renderer/utils/create-dependency-validator';
import cn from 'classnames';
import { pick } from 'lodash';
import { getStepFieldName } from '../../utils/step-field';
import { FieldRender } from '../field-render/field-render';
import styles from './stepper-form.module.css';
import { StepsIndicator } from './steps-indicator';
import { handleSendStepError } from './utils/handle-send-step-error';
import { toDto } from './utils/to-dto';
import { useFormFieldsData } from './utils/use-form-fields-data';
import { sendQuizAnalytics } from '../../utils/quiz-analytics';
export const StepperForm = ({ quizId, quizName, steps, progressBar, background, onLastStepSubmit, onNextStep: _onNextStep, setCurrentStep, }) => {
    const [loading, setLoading] = useState(false);
    const { fieldsValidators, fieldsMap, initialValues } = useFormFieldsData({
        steps,
    });
    const onSubmit = async (data, helpers) => {
        try {
            setLoading(true);
            const { fields } = toDto(data, fieldsMap);
            await onLastStepSubmit({
                formData: data,
                fields,
            });
        }
        catch (err) {
            console.error(err);
        }
        finally {
            setLoading(false);
            helpers.resetForm();
        }
    };
    const onNextStep = async (data) => {
        if (_onNextStep) {
            try {
                const { formData, lastStep, step, stepIndex } = data;
                setLoading(true);
                const { fields } = toDto(pick(formData, [getStepFieldName(step)]), fieldsMap);
                await (_onNextStep === null || _onNextStep === void 0 ? void 0 : _onNextStep({ formData, lastStep, step, stepIndex, fields }));
            }
            catch (err) {
                handleSendStepError(err);
            }
            finally {
                setLoading(false);
            }
        }
    };
    const validate = (fieldsNames) => {
        const errors = Object.entries(fieldsNames).reduce((acc, [name, value]) => {
            acc[name] = fieldsValidators[name](value, fieldsNames);
            return acc;
        }, {});
        return JSON.parse(JSON.stringify(errors));
    };
    return (React.createElement(Formik, { initialValues: initialValues, onSubmit: onSubmit, validate: validate, enableReinitialize: true, isInitialValid: false }, (props) => {
        return (React.createElement(FormComponent, { quizId: quizId, quizName: quizName, loading: loading, onNextStep: onNextStep, steps: steps, progressBar: progressBar, background: background, setCurrentStep: setCurrentStep, ...props }));
    }));
};
function filterSteps({ formValues, steps }) {
    return steps.filter((step) => {
        const validator = createDependencyValidator(step.condition);
        if (validator) {
            return validator(formValues);
        }
        return true;
    });
}
function FormComponent({ quizId, quizName, loading, steps: _steps, values: formValues, submitForm, setFieldValue, setFieldTouched, initialValues, getFieldMeta, validateForm, progressBar, background, onNextStep: _onNextStep, setCurrentStep, }) {
    const [state, setState] = useState(() => {
        return {
            steps: filterSteps({ steps: _steps, formValues }),
            currentStepIndex: 0,
        };
    });
    const formValuesRef = useRef();
    formValuesRef.current = formValues;
    useEffect(() => {
        setState({
            steps: filterSteps({ steps: _steps, formValues: formValuesRef.current }),
            currentStepIndex: 0,
        });
    }, [_steps]);
    useEffect(() => {
        validateForm();
    }, [validateForm]);
    const { currentStepIndex, steps } = state;
    const currentStep = steps[currentStepIndex];
    const fieldMeta = getFieldMeta(getStepFieldName(currentStep));
    const isValid = !fieldMeta.error;
    const onNextStep = async () => {
        if (!fieldMeta.error) {
            const newSteps = filterSteps({ steps: _steps, formValues });
            const newStepIndex = currentStepIndex + 1;
            const lastStep = !newSteps[newStepIndex];
            setCurrentStep((prevStep) => +prevStep + 1);
            sendQuizAnalytics({
                quizName,
                quizId,
                quizStep: newStepIndex,
                quizChoice: formValues[getStepFieldName(currentStep)],
                quizAction: 'continue',
            });
            await (_onNextStep === null || _onNextStep === void 0 ? void 0 : _onNextStep({ formData: formValues, lastStep, step: currentStep, stepIndex: currentStepIndex }));
            if (lastStep) {
                submitForm();
            }
            else {
                setState({
                    currentStepIndex: newStepIndex,
                    steps: newSteps,
                });
            }
        }
    };
    const prevStep = () => {
        setCurrentStep((prevStep) => +prevStep - 1);
        sendQuizAnalytics({ quizName, quizId, quizStep: currentStepIndex + 1, quizAction: 'back' });
        setState((prev) => {
            return {
                ...prev,
                currentStepIndex: prev.currentStepIndex - 1,
            };
        });
        const fieldName = getStepFieldName(currentStep);
        setFieldValue(fieldName, initialValues[fieldName] || '');
        setFieldTouched(fieldName, false);
    };
    return (React.createElement(Form, { className: styles.form },
        React.createElement(StepsIndicator, { currentStepIndex: currentStepIndex, progressBar: progressBar, background: background, stepsLength: state.steps.length }),
        React.createElement("div", { className: styles.stepContainer },
            currentStep.description && (React.createElement("div", { className: styles.stepDescription },
                React.createElement(Markdown, { startHeader: 3, source: currentStep.description, minimalHeadingStyleLevel: 3, sizeVariant: MarkdownSize.Quiz }),
                currentStep.type === 'checkbox-group' && (React.createElement("div", { className: styles.stepFieldLabel }, "\u041C\u043E\u0436\u0435\u0442\u0435 \u0432\u044B\u0431\u0440\u0430\u0442\u044C \u043D\u0435\u0441\u043A\u043E\u043B\u044C\u043A\u043E \u0432\u0430\u0440\u0438\u0430\u043D\u0442\u043E\u0432 \u043E\u0442\u0432\u0435\u0442\u0430")))),
            React.createElement("div", { className: cn(styles.fieldWrapper, styles[`field-${currentStep.type}`]) },
                React.createElement(FieldRender, { ...currentStep }))),
        React.createElement("div", { className: styles.navigationButtons },
            state.currentStepIndex > 0 && (React.createElement("div", { className: styles.buttonPrev },
                React.createElement("div", { className: styles.buttonPrevWithIcon },
                    React.createElement(Button, { onClick: prevStep, disabled: loading, size: "l", variant: "secondary", icon: "arrow-left" })),
                React.createElement("div", { className: styles.buttonPrevWithText },
                    React.createElement(Button, { onClick: prevStep, disabled: loading, size: "l", variant: "secondary", fullWidth: true }, "\u041D\u0430\u0437\u0430\u0434")))),
            React.createElement("div", { className: styles.buttonNext },
                React.createElement(Button, { onClick: onNextStep, loading: loading, disabled: !isValid || loading, fullWidth: true, size: "l" }, "\u0414\u0430\u043B\u0435\u0435")))));
}
//# sourceMappingURL=stepper-form.js.map